import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { fmtDateTime } from "@/lib/format";
import {
  Sparkles, Plus, Pencil, Trash2, X, Inbox, Check, Ban, RefreshCw,
} from "lucide-react";
import ModuleHint from "@/components/ModuleHint";

const EMPTY_EXT = {
  key: "", name: "", description: "", category: "Sonstige",
  monthly_price_eur: 0, yearly_price_eur: 0, setup_price_eur: 0,
  active: true, available_for_packages: [], required_modules: [],
  trial_enabled: false, trial_days: 0, visible_in_tenant_tab: true,
  icon: "Sparkles", sort_order: 100,
};

export default function SuperAdminExtensions() {
  const { user } = useAuth();
  const [tab, setTab] = useState("catalog");
  const [catalog, setCatalog] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [decisionFor, setDecisionFor] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        api.get("/extensions/catalog"),
        api.get("/extensions/requests?status=pending"),
      ]);
      setCatalog(c.data || []);
      setRequests(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Laden fehlgeschlagen");
    } finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function deleteExt(e) {
    if (!window.confirm(`Extension "${e.name}" löschen?`)) return;
    try {
      await api.delete(`/extensions/catalog/${e.id}`);
      toast.success("Gelöscht");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Löschen fehlgeschlagen");
    }
  }

  if (user && !user.is_super_admin) return <Navigate to="/" replace />;

  return (
    <div className="p-6 md:p-8" data-testid="super-admin-extensions-page">
      <ModuleHint moduleKey="extensions" />
      <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold text-zinc-100 inline-flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-emerald-400" />
            Erweiterungen & Add-ons
            <span className="ml-2 text-[9px] uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/15 text-amber-400 border border-amber-500/30">Super-Admin</span>
          </h2>
          <p className="text-xs text-zinc-500 mt-1">
            Verwalten Sie den Katalog der bezahlten Add-ons und genehmigen Sie Aktivierungs-Anfragen.
          </p>
        </div>
        {tab === "catalog" && (
          <button onClick={() => setEditing({ ...EMPTY_EXT })}
            data-testid="ext-new-btn"
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded">
            <Plus className="w-4 h-4" /> Neue Extension
          </button>
        )}
      </div>

      <div className="flex gap-1 border-b border-zinc-800 mb-4">
        <TabBtn active={tab === "catalog"} onClick={() => setTab("catalog")} testid="tab-catalog">
          Katalog ({catalog.length})
        </TabBtn>
        <TabBtn active={tab === "requests"} onClick={() => setTab("requests")} testid="tab-requests">
          Anfragen
          {requests.length > 0 && (
            <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-zinc-950 text-[10px] font-bold">
              {requests.length}
            </span>
          )}
        </TabBtn>
      </div>

      {loading ? (
        <div className="text-zinc-500 text-sm p-8 text-center">Lade…</div>
      ) : tab === "catalog" ? (
        <CatalogTable items={catalog} onEdit={setEditing} onDelete={deleteExt} />
      ) : (
        <RequestsList items={requests} onDecide={setDecisionFor} />
      )}

      {editing && (
        <ExtensionEditor extension={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}

      {decisionFor && (
        <DecisionModal request={decisionFor}
          onClose={() => setDecisionFor(null)}
          onDone={() => { setDecisionFor(null); load(); }} />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
        active ? "text-emerald-300 border-emerald-500"
               : "text-zinc-400 border-transparent hover:text-zinc-200"
      }`}>{children}</button>
  );
}

function CatalogTable({ items, onEdit, onDelete }) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
          <th className="px-4 py-3 text-left">Name</th>
          <th className="px-4 py-3 text-left">Key</th>
          <th className="px-4 py-3 text-left">Kategorie</th>
          <th className="px-4 py-3 text-right">Mtl. €</th>
          <th className="px-4 py-3 text-right">Jährl. €</th>
          <th className="px-4 py-3 text-center">Trial</th>
          <th className="px-4 py-3 text-center">Sichtbar</th>
          <th className="px-4 py-3 text-center">Status</th>
          <th className="px-4 py-3 text-right">Aktionen</th>
        </tr></thead>
        <tbody>
          {items.map(e => (
            <tr key={e.id} data-testid={`ext-row-${e.key}`} className="border-b border-zinc-800 hover:bg-zinc-800/40">
              <td className="px-4 py-2.5 font-medium text-zinc-100">{e.name}</td>
              <td className="px-4 py-2.5 font-mono text-zinc-400 text-xs">{e.key}</td>
              <td className="px-4 py-2.5 text-zinc-300">{e.category}</td>
              <td className="px-4 py-2.5 text-right font-mono">{e.monthly_price_eur?.toFixed(0)}</td>
              <td className="px-4 py-2.5 text-right font-mono text-zinc-400">{e.yearly_price_eur?.toFixed(0)}</td>
              <td className="px-4 py-2.5 text-center">
                {e.trial_enabled ? <span className="text-blue-300 text-xs">{e.trial_days} Tg</span> : <span className="text-zinc-600">—</span>}
              </td>
              <td className="px-4 py-2.5 text-center">
                {e.visible_in_tenant_tab ? <Check className="w-4 h-4 text-emerald-400 mx-auto" /> : <X className="w-4 h-4 text-zinc-600 mx-auto" />}
              </td>
              <td className="px-4 py-2.5 text-center">
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${
                  e.active ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                           : "bg-zinc-800 text-zinc-500"
                }`}>{e.active ? "Aktiv" : "Inaktiv"}</span>
              </td>
              <td className="px-4 py-2.5 text-right">
                <div className="inline-flex gap-2">
                  <button onClick={() => onEdit(e)} data-testid={`ext-edit-${e.key}`}
                    className="text-zinc-400 hover:text-zinc-100"><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => onDelete(e)} data-testid={`ext-delete-${e.key}`}
                    className="text-zinc-500 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RequestsList({ items, onDecide }) {
  if (items.length === 0) {
    return (
      <div className="text-zinc-500 text-sm p-10 text-center">
        <Inbox className="w-8 h-8 mx-auto mb-2 text-zinc-700" />
        Keine offenen Anfragen.
      </div>
    );
  }
  return (
    <div className="space-y-2">
      {items.map(r => (
        <div key={r.id} data-testid={`req-row-${r.id}`}
          className="bg-zinc-900 border border-zinc-800 rounded p-4 flex items-start gap-4 hover:border-zinc-700">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-medium text-zinc-100">{r.tenant_name}</span>
              <span className="text-xs text-zinc-500 font-mono">{r.tenant_slug}</span>
            </div>
            <div className="text-sm text-zinc-300">
              möchte <strong className="text-emerald-300">{r.extension_name}</strong> aktivieren
            </div>
            {r.note && <div className="mt-1 text-xs text-zinc-400 italic">„{r.note}"</div>}
            <div className="mt-1 text-[10px] text-zinc-500 font-mono">
              {fmtDateTime(r.requested_at)} · {r.requested_by}
            </div>
          </div>
          <button onClick={() => onDecide(r)} data-testid={`req-decide-${r.id}`}
            className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded">
            Entscheiden
          </button>
        </div>
      ))}
    </div>
  );
}

function ExtensionEditor({ extension, onClose, onSaved }) {
  const [form, setForm] = useState(extension);
  const [saving, setSaving] = useState(false);
  const isNew = !extension.id;

  function set(k, v) { setForm(p => ({ ...p, [k]: v })); }

  async function save() {
    if (!form.key.trim() || !form.name.trim()) {
      toast.error("Key und Name sind Pflichtfelder"); return;
    }
    setSaving(true);
    try {
      if (isNew) {
        await api.post("/extensions/catalog", form);
        toast.success("Extension angelegt");
      } else {
        await api.put(`/extensions/catalog/${extension.id}`, form);
        toast.success("Gespeichert");
      }
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="ext-editor">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="p-5 border-b border-zinc-800 flex items-center justify-between sticky top-0 bg-zinc-900">
          <h3 className="text-lg font-semibold text-zinc-100">{isNew ? "Neue Extension" : "Extension bearbeiten"}</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Key (technical, immutable) *">
              <input type="text" value={form.key} disabled={!isNew}
                onChange={(e) => set("key", e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
                placeholder="z.B. premium_export"
                data-testid="ext-form-key"
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100 font-mono disabled:opacity-50" />
            </Field>
            <Field label="Anzeige-Name *">
              <input type="text" value={form.name}
                onChange={(e) => set("name", e.target.value)}
                data-testid="ext-form-name"
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
            </Field>
          </div>
          <Field label="Beschreibung">
            <textarea value={form.description} rows={3}
              onChange={(e) => set("description", e.target.value)}
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Kategorie">
              <input type="text" value={form.category} onChange={(e) => set("category", e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
            </Field>
            <Field label="Icon (lucide)">
              <input type="text" value={form.icon} onChange={(e) => set("icon", e.target.value)}
                placeholder="z.B. Calculator, BookOpen, Database"
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Mtl. Preis €">
              <input type="number" min={0} step="0.01" value={form.monthly_price_eur}
                onChange={(e) => set("monthly_price_eur", parseFloat(e.target.value) || 0)}
                data-testid="ext-form-monthly"
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
            </Field>
            <Field label="Jährl. Preis €">
              <input type="number" min={0} step="0.01" value={form.yearly_price_eur}
                onChange={(e) => set("yearly_price_eur", parseFloat(e.target.value) || 0)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
            </Field>
            <Field label="Setup-Gebühr €">
              <input type="number" min={0} step="0.01" value={form.setup_price_eur}
                onChange={(e) => set("setup_price_eur", parseFloat(e.target.value) || 0)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
            </Field>
          </div>
          <Field label="Verfügbar für Pakete (kommasepariert)">
            <input type="text" value={(form.available_for_packages || []).join(", ")}
              onChange={(e) => set("available_for_packages", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
              placeholder="basic, pro, enterprise"
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
          </Field>
          <Field label="Benötigt Module (kommasepariert)">
            <input type="text" value={(form.required_modules || []).join(", ")}
              onChange={(e) => set("required_modules", e.target.value.split(",").map(s => s.trim()).filter(Boolean))}
              placeholder="mitarbeiter, exports"
              className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" checked={!!form.trial_enabled}
                onChange={(e) => set("trial_enabled", e.target.checked)} />
              Trial aktivierbar
            </label>
            <Field label="Trial-Tage">
              <input type="number" min={0} value={form.trial_days}
                onChange={(e) => set("trial_days", parseInt(e.target.value, 10) || 0)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" checked={!!form.active}
                onChange={(e) => set("active", e.target.checked)} />
              Aktiv (insgesamt)
            </label>
            <label className="inline-flex items-center gap-2 text-sm text-zinc-300">
              <input type="checkbox" checked={!!form.visible_in_tenant_tab}
                onChange={(e) => set("visible_in_tenant_tab", e.target.checked)} />
              Sichtbar in Tenant-Tab
            </label>
          </div>
        </div>
        <div className="p-4 border-t border-zinc-800 flex justify-end gap-2 sticky bottom-0 bg-zinc-900">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
          <button onClick={save} disabled={saving} data-testid="ext-form-save"
            className="px-4 py-2 text-sm bg-emerald-600 hover:bg-emerald-700 disabled:bg-zinc-800 text-white rounded font-medium">
            {saving ? "Speichert…" : (isNew ? "Anlegen" : "Speichern")}
          </button>
        </div>
      </div>
    </div>
  );
}

function DecisionModal({ request, onClose, onDone }) {
  const [decision, setDecision] = useState("approve");
  const [customPrice, setCustomPrice] = useState("");
  const [expiry, setExpiry] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  async function decide() {
    setBusy(true);
    try {
      await api.post(`/extensions/requests/${request.id}/decide`, {
        decision,
        custom_price_monthly: customPrice ? parseFloat(customPrice) : null,
        expiry_date: expiry || null,
        decision_note: note,
      });
      toast.success(decision === "approve" ? "Aktiviert" : "Abgelehnt");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehlgeschlagen");
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="ext-decision-modal">
      <div className="bg-zinc-900 border border-zinc-800 rounded-lg w-full max-w-md p-5">
        <h3 className="text-lg font-semibold text-zinc-100 mb-1">Anfrage entscheiden</h3>
        <p className="text-xs text-zinc-500 mb-4">{request.tenant_name} · {request.extension_name}</p>
        {request.note && <div className="mb-4 text-xs text-zinc-400 italic bg-zinc-950 border border-zinc-800 rounded p-2">„{request.note}"</div>}
        <div className="flex gap-2 mb-4">
          <button onClick={() => setDecision("approve")} data-testid="decision-approve"
            className={`flex-1 px-3 py-2 text-sm rounded border inline-flex items-center justify-center gap-1.5 ${
              decision === "approve" ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-200"
                                     : "border-zinc-800 bg-zinc-950 text-zinc-400"
            }`}>
            <Check className="w-4 h-4" /> Genehmigen
          </button>
          <button onClick={() => setDecision("reject")} data-testid="decision-reject"
            className={`flex-1 px-3 py-2 text-sm rounded border inline-flex items-center justify-center gap-1.5 ${
              decision === "reject" ? "border-red-500/50 bg-red-500/10 text-red-200"
                                    : "border-zinc-800 bg-zinc-950 text-zinc-400"
            }`}>
            <Ban className="w-4 h-4" /> Ablehnen
          </button>
        </div>
        {decision === "approve" && (
          <>
            <Field label="Sonderpreis €/Monat (optional)">
              <input type="number" min={0} step="0.01" value={customPrice} onChange={(e) => setCustomPrice(e.target.value)}
                data-testid="decision-price"
                placeholder="leer = Katalogpreis"
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
            </Field>
            <Field label="Ablaufdatum (optional, ISO)">
              <input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)}
                className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
            </Field>
          </>
        )}
        <Field label="Notiz (intern + Audit-Log)">
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
            data-testid="decision-note"
            className="w-full bg-zinc-950 border border-zinc-800 rounded px-3 py-2 text-sm text-zinc-100" />
        </Field>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-sm bg-zinc-800 hover:bg-zinc-700 rounded">Abbrechen</button>
          <button onClick={decide} disabled={busy} data-testid="decision-submit"
            className={`px-4 py-2 text-sm text-white rounded font-medium ${
              decision === "approve" ? "bg-emerald-600 hover:bg-emerald-700"
                                     : "bg-red-600 hover:bg-red-700"
            }`}>
            {busy ? "…" : (decision === "approve" ? "Genehmigen" : "Ablehnen")}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-3">
      <label className="block text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
